import React from 'react';
import { CloudUploadOutlined } from '@ant-design/icons';
import { Button, Drawer, Form, Space, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getFileImport } from './redux-saga/selectors';
import { fileImportActions, setPlannedDutiesRefMoment } from './redux-saga/actions';
import { FileImportUploadResult } from './steps/FileImportUploadResult';
import { defaultVersion, FileImportSourceVersion, FileImportStatus } from './redux-saga/reducers';
import { FileImportSelectVersionStep } from './steps/FileImportSelectVersionStep';
import { FileImportUploader, FileType } from './steps/FileImportUploader';
import { ATDateTimePicker } from '../../../../../../../components/shared/ATDateTimePicker/ATDateTimePicker';

interface FOSImportProps {}

export const FileImport: React.FC<FOSImportProps> = () => {
  const dispatch = useDispatch();
  const {
    isShowDrawer,
    isLoading,
    uploadResult,
    dutyTimeFile,
    flightInfoFile,
    version,
    plannedDutiesRefMoment,
  } = useSelector(getFileImport);

  const isFilesTheSame = () => {
    if (!dutyTimeFile || !flightInfoFile) {
      return false;
    }

    return dutyTimeFile.name === flightInfoFile.name;
  };

  const isShowUploadButton = () => {
    if (version === FileImportSourceVersion.v1) {
      if (isFilesTheSame()) {
        return false;
      }
      return !!dutyTimeFile && !!flightInfoFile;
    }

    if (version === FileImportSourceVersion.v2) {
      return !!dutyTimeFile;
    }

    return false;
  };

  return (
    <>
      <Button
        onClick={() => {
          dispatch(fileImportActions.setDrawerVisible(true));
        }}
        type="primary"
      >
        <CloudUploadOutlined /> File Import
      </Button>

      <Drawer
        width={600}
        title="File Import"
        placement="right"
        onClose={() => {
          dispatch(fileImportActions.setDrawerVisible(false));
          dispatch(fileImportActions.setState({ version: defaultVersion }));
        }}
        open={isShowDrawer}
      >
        {uploadResult.status === FileImportStatus.NONE && (
          <Spin spinning={isLoading}>
            <Space
              direction="vertical"
              size="large"
              style={{
                display: 'flex',
              }}
            >
              <FileImportSelectVersionStep />

              {version === FileImportSourceVersion.v1 && (
                <>
                  <FileImportUploader
                    btnText={'Select Duty Time File'}
                    titleText={'Step 1 : Upload Duty Time File (Crew Duty Rpt)'}
                    field={'dutyTimeFile'}
                    type={FileType.XLS}
                  />

                  {!!dutyTimeFile && (
                    <>
                      <FileImportUploader
                        btnText={'Select Flight Info File'}
                        titleText={'Step 2 : Upload Flight Info File (Crew Leg Info)'}
                        field={'flightInfoFile'}
                        type={FileType.XLS}
                      />
                    </>
                  )}

                  {!!flightInfoFile && (
                    <Form layout="vertical">
                      <Form.Item label="Create Planned Duty Time after: ">
                        <ATDateTimePicker
                          value={plannedDutiesRefMoment}
                          onChange={val => dispatch(setPlannedDutiesRefMoment(val))}
                        />
                      </Form.Item>
                    </Form>
                  )}
                </>
              )}

              {version === FileImportSourceVersion.v2 && (
                <>
                  <FileImportUploader
                    btnText={'Select File'}
                    titleText={'Upload FOS XLSM File'}
                    field={'dutyTimeFile'}
                    type={FileType.XLSM}
                  />
                </>
              )}

              {version === FileImportSourceVersion.v1 &&
                !!dutyTimeFile &&
                !!flightInfoFile &&
                isFilesTheSame() && (
                  <p style={{ color: 'red' }}>
                    <b>NOTE:</b> The selected files are the same. Please make sure to select the
                    correct files.
                  </p>
                )}

              {isShowUploadButton() && (
                <Button
                  loading={isLoading}
                  type="primary"
                  onClick={() => dispatch(fileImportActions.upload())}
                >
                  Upload
                </Button>
              )}
            </Space>
          </Spin>
        )}

        {uploadResult.status !== FileImportStatus.NONE && <FileImportUploadResult />}
      </Drawer>
    </>
  );
};
